import { useState } from "react";
import { useTranslation } from "react-i18next";
import googleAuthenticator from "../../../../assets/Google_Authenticator.svg.png";
import microsoftAuthenticator from "../../../../assets/microsoft-authenticator.png";
import authy from "../../../../assets/authy.png";
import rush from "../../../../assets/rush.png";
import AuthPrimaryButton from "./AuthPrimaryButton";
import { OtpPreferenceSelector } from "./OtpPreferenceSelector";
import { useNavigate } from "react-router-dom";
import { useOtpPreference } from "../../application/hooks/useOtpPreference";
import { useSession } from "../../application/hooks/useSession";
import {
  OTP_PREFERENCE_EMAIL,
  OTP_PREFERENCE_TOTP,
  type OtpPreference,
} from "../../domain/constants/otpConstants";

const SetupTwoFA = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { setOtpPreference, loading: savingPreference } = useOtpPreference();
  const { getUserEmail, getIsTotpEnabled } = useSession();
  const [preference, setPreference] = useState<OtpPreference>(OTP_PREFERENCE_TOTP);
  const [error, setError] = useState<string | null>(null);

  const loading = savingPreference;

  const totpAlreadyEnabled = getIsTotpEnabled() ?? false;

  const handleContinue = async () => {
    setError(null);

    if (preference === OTP_PREFERENCE_EMAIL) {
      const result = await setOtpPreference(
        OTP_PREFERENCE_EMAIL,
        getIsTotpEnabled() ?? false,
      );

      if (!result.ok) {
        setError("message" in result ? result.message : t("twoFaSetup.genericError"));
        return;
      }

      navigate("/twofa-success", {
        state: { otpPreference: OTP_PREFERENCE_EMAIL },
      });
      return;
    }

    const result = await setOtpPreference(
      OTP_PREFERENCE_TOTP,
      getIsTotpEnabled() ?? false,
    );

    if ("needsTotpSetup" in result && result.needsTotpSetup) {
      navigate("/scan-qr");
      return;
    }

    if (!result.ok) {
      setError("message" in result ? result.message : "Unable to save preference.");
      return;
    }

    navigate("/twofa-success", {
      state: { otpPreference: OTP_PREFERENCE_TOTP },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm font-medium text-gray-800">
        {t("twoFaSetup.chooseMethod")}
      </p>

      <OtpPreferenceSelector
        value={preference}
        onChange={setPreference}
        disabled={loading}
      />

      {preference === OTP_PREFERENCE_TOTP && !totpAlreadyEnabled && (
        <>
          <p className="text-sm font-medium text-gray-800">{t("twoFaSetup.whatYoullNeed")}</p>
          <div className="flex flex-col gap-2 rounded-xl bg-blue-50 p-4">
            <div className="flex flex-row gap-2">
              <img src={googleAuthenticator} alt="google-authenticator" className="h-6 w-6" />
              <img src={microsoftAuthenticator} alt="microsoft-authenticator" className="h-6 w-6" />
              <img src={authy} alt="authy" className="h-6 w-6" />
            </div>
            <div className="text-sm text-gray-700">
              {t("twoFaSetup.authenticatorAppsHint")}
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-xl bg-blue-50 p-4 text-sm text-gray-700">
            <img src={rush} alt="rush" className="h-6 w-6" />
            <p>{t("twoFaSetup.twoMinutesHint")}</p>
          </div>
        </>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <AuthPrimaryButton
        type="button"
        onClick={() => void handleContinue()}
        loading={loading}
      >
        {preference === OTP_PREFERENCE_TOTP
          ? totpAlreadyEnabled
            ? t("twoFaSetup.continueWithAuthenticator")
            : t("twoFaSetup.setUpAuthenticator")
          : t("twoFaSetup.saveEmailSignIn")}
      </AuthPrimaryButton>
    </div>
  );
};

export default SetupTwoFA;
